// src/pages/CustomerSupport.jsx
import React, { useState } from "react";
import { db } from "../firebase";
import {
  doc,
  setDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import "../style/CustomerSupport.css";

const categories = [
  "Returns",
  "Product Issue",
  "E-commerce",
  "Inquiry",
  "Bulk Order",
  "System Related",
  "Other",
];

export default function CustomerSupport() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    category: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState("");
  const [phoneError, setPhoneError] = useState(false);

  // ✅ Generate Ticket ID with YEAR RESET logic
  const generateTicketId = async () => {
    const year = new Date().getFullYear();
    const counterRef = doc(db, "supportMeta", "ticketCounter");

    const newNumber = await runTransaction(db, async (transaction) => {
      const counterSnap = await transaction.get(counterRef);

      if (!counterSnap.exists()) {
        throw new Error("ticketCounter doc is missing.");
      }

      const data = counterSnap.data();
      const lastYear = data.lastYear;
      let lastNumber = data.lastNumber || 0;

      // 🔁 YEAR CHANGE → RESET COUNTER
      if (lastYear !== year) {
        transaction.update(counterRef, {
          lastYear: year,
          lastNumber: 1,
        });
        return 1;
      }

      // ➕ SAME YEAR → INCREMENT
      const nextNumber = lastNumber + 1;
      transaction.update(counterRef, {
        lastNumber: nextNumber,
      });

      return nextNumber;
    });

    const paddedNumber = String(newNumber).padStart(6, "0");
    return `GETCO-${year}-${paddedNumber}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "phone") {
      setPhoneError(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.phone.length !== 10) {
      setPhoneError(true);
      return;
    }

    setSubmitting(true);
    try {
      const newTicketId = await generateTicketId();

      await setDoc(doc(db, "supportTickets", newTicketId), {
        ...form,
        status: "open",
        createdAt: serverTimestamp(),
        ticketId: newTicketId,
      });

      setTicketId(newTicketId);

      setForm({
        name: "",
        phone: "",
        category: "",
        description: "",
      });
    } catch (err) {
      console.error("Error submitting ticket:", err);
      alert("❌ Failed to submit ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="support-page">
      <div className="support-container">
        <h1>Customer Support</h1>
        <p>Please fill the form below to raise a support request.</p>

        <form className="support-form" onSubmit={handleSubmit}>
          <input
            type="text"
            name="name"
            placeholder="Your Name"
            value={form.name}
            onChange={handleChange}
            required
          />

          <input
            type="tel"
            name="phone"
            placeholder="Your Phone Number"
            value={form.phone}
            onChange={handleChange}
            required
            className={phoneError ? "error-input" : ""}
          />
          {phoneError && (
            <p className="error-text">
              ⚠️ Phone Number must be exactly 10 digits
            </p>
          )}

          <select
            name="category"
            value={form.category}
            onChange={handleChange}
            required
          >
            <option value="">Select Category</option>
            {categories.map((c, i) => (
              <option key={i} value={c}>
                {c}
              </option>
            ))}
          </select>

          <textarea
            name="description"
            placeholder="Briefly describe your problem"
            value={form.description}
            onChange={handleChange}
            required
          />

          <button type="submit" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit Ticket"}
          </button>
        </form>

        {/* ✅ Success Modal */}
        {ticketId && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button
                className="modal-close"
                onClick={() => setTicketId("")}
              >
                ✖
              </button>
              <h2>🎉 Ticket Created Successfully</h2>
              <p>
                <strong>Ticket ID:</strong> {ticketId}
              </p>
              <p>This ticket ID will also be shared with you via WhatsApp.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
